import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, Phone, MapPin, MessageSquare } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/enquiries', {
        ...formData,
        enquiry_type: 'contact',
      });
      toast.success('Thank you! We\'ll get back to you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const whatsappNumber = '+919619977421';
  const whatsappMessage = encodeURIComponent('Hi! I would like to know more about Rudra Apparel.');

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="section-spacing bg-brand-timberwolf" data-testid="contact-hero">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-brand-pearl leading-none mb-6" data-testid="contact-hero-heading">
              Get in Touch
            </h1>
            <p className="text-base sm:text-lg text-brand-walnut max-w-3xl mx-auto leading-relaxed">
              Have a question or ready to start your project? We're here to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-spacing" data-testid="contact-content">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                <div>
                  <h3 className="text-2xl font-heading font-bold text-brand-pearl mb-6">Contact Information</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4" data-testid="contact-email">
                      <div className="w-12 h-12 bg-brand-timberwolf rounded-sm flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-brand-pearl" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-brand-pearl mb-1">Email</p>
                        <a href="mailto:info@rudraapparel.com" className="text-brand-walnut hover:text-brand-pearl">
                          info@rudraapparel.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4" data-testid="contact-phone">
                      <div className="w-12 h-12 bg-brand-timberwolf rounded-sm flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-brand-pearl" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-brand-pearl mb-1">Phone</p>
                        <a href="tel:+919876543210" className="text-brand-walnut hover:text-brand-pearl">
                          +91 98765 43210
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4" data-testid="contact-address">
                      <div className="w-12 h-12 bg-brand-timberwolf rounded-sm flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-brand-pearl" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-brand-pearl mb-1">Address</p>
                        <p className="text-brand-walnut">
                          Manufacturing Unit,<br />
                          Andheri East, Mumbai,<br />
                          Maharashtra 400069, India
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <a
                  href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-contact-btn"
                  className="flex items-center justify-center px-6 py-3 bg-brand-green text-white font-medium rounded-sm hover:bg-brand-green/90 transition-colors"
                >
                  <MessageSquare className="mr-2 w-5 h-5" />
                  Chat on WhatsApp
                </a>
              </motion.div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.form
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                onSubmit={handleSubmit}
                className="bg-brand-timberwolf rounded-sm border border-brand-pearl/10 p-8"
                data-testid="contact-form"
              >
                <h3 className="text-2xl font-heading font-bold text-brand-pearl mb-6">Send us a Message</h3>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="name">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      data-testid="contact-form-name"
                      className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="email">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        data-testid="contact-form-email"
                        className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="phone">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        data-testid="contact-form-phone"
                        className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="company">
                      Company/Organization
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      data-testid="contact-form-company"
                      className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="message">
                      Your Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      data-testid="contact-form-message"
                      rows="6"
                      placeholder="Tell us about your requirements..."
                      className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="w-full flex items-center justify-center px-8 py-4 bg-brand-pearl text-brand-ivory font-medium rounded-sm hover:bg-brand-walnut transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Sending...' : (
                      <>
                        <Send className="mr-2 w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </motion.form>
            </div>
          </div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-12 rounded-sm overflow-hidden border border-brand-pearl/10"
            data-testid="contact-map"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3769.8158268970304!2d72.85360431490215!3d19.117502887071067!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c83be2f3a2d3%3A0xb0d5c7be0e43e8cf!2sAndheri%20East%2C%20Mumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1609459200000!5m2!1sen!2sin"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="Rudra Apparel Location"
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;